/**
 * MOCKUP STANDALONE — Super Admin "Command Center"
 * Pagina isolata, solo anteprima visiva. Route: /mockup/admin
 */
import React from 'react';
import {
  LayoutDashboard, Building2, Users, Bell, Search, Sparkles, Zap, Wrench,
  ArrowUpRight, AlertTriangle, TrendingUp, Activity, FileText, Calendar,
  Crown, ChevronRight, Siren, Plus, Settings, BarChart3, Flame, Droplets,
  Map, ListChecks, CircleDot
} from 'lucide-react';

import { C } from './mockup/_tokens';

const fontHead = "'Inter', 'Geist', system-ui, sans-serif";

export default function MockupSuperAdmin() {
  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: C.text, fontFamily: 'Inter, system-ui',
                  backgroundImage: `
                    radial-gradient(ellipse 60% 40% at 20% 0%, rgba(212,175,55,0.06), transparent),
                    radial-gradient(ellipse 50% 30% at 90% 100%, rgba(56,189,248,0.05), transparent)`,
    }}>
      <div style={{
        position: 'fixed', top: 10, left: '50%', transform: 'translateX(-50%)',
        background: 'rgba(212,175,55,0.15)', color: C.gold, border: `1px solid ${C.gold}`,
        padding: '4px 14px', borderRadius: 999, fontSize: 11, letterSpacing: '0.2em',
        fontWeight: 600, zIndex: 100, backdropFilter: 'blur(12px)',
      }}>MOCKUP · LOOK DEL FUTURO · SUPER ADMIN</div>

      <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', minHeight: '100vh' }}>
        <Sidebar />
        <main style={{ padding: '52px 36px 36px' }}>
          <TopBar />
          <Hero />
          <BentoTop />
          <BentoMid />
          <BottomRow />
          <div style={{ marginTop: 44, paddingTop: 18, borderTop: `1px solid ${C.border}`,
                        fontSize: 12, color: C.textSec, textAlign: 'center', letterSpacing: '0.1em' }}>
            HostFix · Command Center · Concept v1 — Multi-hotel, hospitality-coded, dark-mode dominant
          </div>
        </main>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- SIDEBAR
const Sidebar = () => {
  const items = [
    { icon: LayoutDashboard, label: 'Command Center', active: true },
    { icon: Building2, label: 'Hotel del Gruppo' },
    { icon: ListChecks, label: 'Tutti i Ticket', badge: 47 },
    { icon: Calendar, label: 'Manutenzione' },
    { icon: BarChart3, label: 'Consumi & Energia' },
    { icon: Map, label: 'Heatmap problemi' },
    { icon: Users, label: 'Utenti & Ruoli' },
    { icon: FileText, label: 'Documenti' },
    { icon: Siren, label: 'Rubrica Emergenze', danger: true },
    { icon: Settings, label: 'Impostazioni' },
  ];
  return (
    <aside style={{
      background: C.surfDeep, borderRight: `1px solid ${C.border}`,
      padding: 24, display: 'flex', flexDirection: 'column', gap: 4,
      position: 'sticky', top: 0, height: '100vh',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 32, paddingTop: 10 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10,
          background: `linear-gradient(135deg, ${C.gold} 0%, #B8941F 100%)`,
          display: 'grid', placeItems: 'center', boxShadow: `0 0 24px ${C.goldGlow}`,
        }}>
          <Wrench size={18} color={C.bg} strokeWidth={2.5} />
        </div>
        <div>
          <div style={{ fontFamily: fontHead, fontSize: 17, fontWeight: 600, letterSpacing: '-0.02em' }}>
            HostFix<span style={{ color: C.gold }}>.</span>
          </div>
          <div style={{ fontSize: 10, color: C.textSec, letterSpacing: '0.15em', textTransform: 'uppercase' }}>
            Group Console
          </div>
        </div>
      </div>

      <div style={{ fontSize: 10, color: C.textSec, letterSpacing: '0.2em',
                    textTransform: 'uppercase', marginBottom: 10, paddingLeft: 4 }}>Navigazione</div>

      {items.map((it, i) => (
        <div key={i} style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
          background: it.active ? `${C.gold}15` : 'transparent',
          position: 'relative',
          color: it.danger ? '#FCA5A5' : (it.active ? C.gold : C.textSec),
          fontWeight: it.active ? 600 : 400, fontSize: 13.5,
          border: it.active ? `1px solid ${C.gold}33` : '1px solid transparent',
        }}>
          {it.active && <div style={{
            position: 'absolute', left: -24, top: 8, bottom: 8, width: 3,
            background: C.gold, borderRadius: '0 2px 2px 0', boxShadow: `0 0 8px ${C.gold}`,
          }} />}
          <it.icon size={16} strokeWidth={it.active ? 2.5 : 1.8} />
          <span style={{ flex: 1 }}>{it.label}</span>
          {it.badge && <span style={{
            background: C.terra, color: '#fff', fontSize: 10, fontWeight: 700,
            padding: '2px 6px', borderRadius: 6,
          }}>{it.badge}</span>}
        </div>
      ))}

      <div style={{ flex: 1 }} />

      <div style={{
        padding: 14, borderRadius: 12, background: C.surf,
        border: `1px solid ${C.border}`, marginTop: 20,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <Crown size={13} color={C.gold} />
          <span style={{ fontSize: 10.5, letterSpacing: '0.15em', textTransform: 'uppercase',
                         color: C.gold, fontWeight: 600 }}>Super Admin</span>
        </div>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2 }}>Marco Rossi</div>
        <div style={{ fontSize: 11, color: C.textSec }}>Invest Hospitality · Gruppo</div>
      </div>
    </aside>
  );
};

// ---------------------------------------------------------------- TOP BAR
const TopBar = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 32 }}>
    <div>
      <div style={{ fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase',
                    color: C.textSec, fontWeight: 500, marginBottom: 4 }}>
        11 Giugno 2026 · 09:42 · Invest Hospitality
      </div>
      <h1 style={{ fontFamily: fontHead, fontSize: 38, fontWeight: 600, margin: 0,
                   letterSpacing: '-0.025em', lineHeight: 1 }}>
        Command Center
      </h1>
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px',
        background: C.surf, borderRadius: 10, border: `1px solid ${C.border}`, minWidth: 320,
      }}>
        <Search size={14} color={C.textSec} />
        <span style={{ flex: 1, color: C.textSec, fontSize: 13 }}>Cerca hotel, ticket, utente...</span>
      </div>
      <button style={{ position: 'relative', width: 38, height: 38, borderRadius: 10,
                       background: C.surf, border: `1px solid ${C.border}`,
                       display: 'grid', placeItems: 'center', cursor: 'pointer' }}>
        <Bell size={16} color={C.text} />
        <span style={{ position: 'absolute', top: 7, right: 7, width: 8, height: 8,
                       borderRadius: '50%', background: C.terra, boxShadow: `0 0 8px ${C.terra}` }} />
      </button>
      <button style={{
        background: `linear-gradient(135deg, ${C.gold}, #B8941F)`, color: C.bg, border: 'none',
        padding: '9px 16px', borderRadius: 10, fontWeight: 600, fontSize: 13, cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', gap: 6,
        boxShadow: `0 4px 24px ${C.goldGlow}`,
      }}>
        <Plus size={14} /> Nuovo Hotel
      </button>
    </div>
  </div>
);

// ---------------------------------------------------------------- HERO INSIGHT
const Hero = () => (
  <div style={{
    padding: '24px 28px', borderRadius: 22,
    background: `linear-gradient(135deg, ${C.gold}11, transparent 60%), ${C.surf}`,
    border: `1px solid ${C.gold}33`,
    marginBottom: 24, position: 'relative', overflow: 'hidden',
  }}>
    <div style={{ position: 'absolute', top: 0, right: 0, width: 400, height: 200,
                  background: `radial-gradient(circle at top right, ${C.goldGlow}, transparent 60%)`,
                  pointerEvents: 'none' }} />
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
      <span style={{ fontSize: 10.5, letterSpacing: '0.18em', textTransform: 'uppercase',
                     color: C.gold, fontWeight: 600 }}>Riepilogo gruppo · 11 Giugno 2026</span>
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24, alignItems: 'center' }}>
      <div style={{ fontFamily: fontHead, fontSize: 22, fontWeight: 500, lineHeight: 1.35, letterSpacing: '-0.01em' }}>
        Gruppo in salute. <span style={{ color: C.gold }}>3 hotel</span> hanno migliorato l&apos;indice efficienza energetica negli ultimi 30 giorni.
        Attenzione su <span style={{ color: C.terra }}>Hotel Galleria</span>: consumi acqua +28% rispetto alla media trimestrale.
      </div>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button style={{
          background: 'transparent', color: C.gold, border: `1px solid ${C.gold}55`,
          padding: '10px 16px', borderRadius: 10, fontWeight: 600, fontSize: 13, cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: 6,
        }}>Apri report <ArrowUpRight size={14} /></button>
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------- BENTO TOP
const BentoTop = () => (
  <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
    {/* Efficiency Trend Chart (large) */}
    <div style={{ background: C.surf, borderRadius: 20, padding: 24,
                  border: `1px solid ${C.border}`, gridRow: 'span 2' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 10.5, letterSpacing: '0.18em', textTransform: 'uppercase',
                        color: C.textSec, fontWeight: 600, marginBottom: 4 }}>Indice Efficienza Gruppo</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 14 }}>
            <span style={{ fontFamily: fontHead, fontSize: 56, fontWeight: 600,
                           letterSpacing: '-0.04em', lineHeight: 1, color: C.gold }}>78.4</span>
            <span style={{ fontSize: 11, color: C.emerald, fontWeight: 600,
                           padding: '3px 8px', background: `${C.emerald}22`, borderRadius: 5 }}>
              +2.3 vs Apr
            </span>
          </div>
          <div style={{ fontSize: 12, color: C.textSec, marginTop: 6 }}>Media pesata · 6 hotel · finestra mobile 12 mesi</div>
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {['6M', '1A', '2A', 'Max'].map((p, i) => (
            <button key={p} style={{
              padding: '4px 10px', borderRadius: 6, fontSize: 11,
              background: i === 1 ? `${C.gold}22` : 'transparent',
              color: i === 1 ? C.gold : C.textSec,
              border: `1px solid ${i === 1 ? C.gold + '55' : C.border}`, cursor: 'pointer',
            }}>{p}</button>
          ))}
        </div>
      </div>
      <TrendChart />
    </div>

    {/* KPI Quad */}
    <KpiCard label="Hotel attivi" value="6" sub="2 all-electric" accent={C.cyan} />
    <KpiCard label="Ticket aperti" value="47" sub="3 urgenti · 12 alta" accent={C.terra} pulse />
    <KpiCard label="Manutenzioni" value="28" sub="4 in scadenza" accent={C.gold} />
    <KpiCard label="Alert consumi" value="2" sub="Galleria · acqua" accent={C.terra} />
  </div>
);

const TrendChart = () => {
  const data = [72, 73, 71, 74, 75, 73, 76, 75, 77, 76, 78, 78.4];
  const W = 520, H = 180, PAD = 30;
  const max = 85, min = 65;
  const labels = ['Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic', 'Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu'];
  const pts = data.map((v, i) => {
    const x = PAD + (i / (data.length - 1)) * (W - 2 * PAD);
    const y = PAD + (1 - (v - min) / (max - min)) * (H - 2 * PAD);
    return [x, y];
  });
  const path = pts.map((p, i) => (i === 0 ? `M${p[0]},${p[1]}` : `L${p[0]},${p[1]}`)).join(' ');
  const area = path + ` L${pts[pts.length - 1][0]},${H - PAD} L${pts[0][0]},${H - PAD} Z`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: H }}>
      <defs>
        <linearGradient id="grad1" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={C.gold} stopOpacity="0.4" />
          <stop offset="100%" stopColor={C.gold} stopOpacity="0" />
        </linearGradient>
      </defs>
      {[0, 1, 2, 3, 4].map(i => (
        <line key={i} x1={PAD} x2={W - PAD} y1={PAD + i * (H - 2 * PAD) / 4} y2={PAD + i * (H - 2 * PAD) / 4}
              stroke={C.border} strokeDasharray="2 3" />
      ))}
      <path d={area} fill="url(#grad1)" />
      <path d={path} stroke={C.gold} strokeWidth="2" fill="none" />
      {pts.map((p, i) => (
        <circle key={i} cx={p[0]} cy={p[1]} r={i === pts.length - 1 ? 4.5 : 2.5}
                fill={i === pts.length - 1 ? C.gold : C.surf} stroke={C.gold} strokeWidth="1.5" />
      ))}
      {labels.map((l, i) => {
        const x = PAD + (i / (labels.length - 1)) * (W - 2 * PAD);
        return <text key={l} x={x} y={H - 8} fill={C.textSec} fontSize="10" textAnchor="middle">{l}</text>;
      })}
    </svg>
  );
};

const KpiCard = ({ label, value, sub, accent, pulse }) => (
  <div style={{ background: C.surf, borderRadius: 20, padding: 22,
                border: `1px solid ${C.border}`, position: 'relative' }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
      <span style={{ fontSize: 10.5, letterSpacing: '0.18em', textTransform: 'uppercase',
                     color: C.textSec, fontWeight: 600 }}>{label}</span>
      {pulse && <span style={{ width: 8, height: 8, borderRadius: '50%', background: accent,
                               boxShadow: `0 0 12px ${accent}` }} />}
    </div>
    <div style={{ fontFamily: fontHead, fontSize: 44, fontWeight: 600,
                  letterSpacing: '-0.04em', lineHeight: 1, color: accent }}>{value}</div>
    <div style={{ color: C.textSec, fontSize: 12, marginTop: 8 }}>{sub}</div>
  </div>
);

// ---------------------------------------------------------------- BENTO MID
const BentoMid = () => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
    <HotelRanking />
    <CriticalFeed />
  </div>
);

const hotels = [
  { name: 'Hotel Scarlatti', city: 'Roma', eff: 87.2, ae: false, rooms: 86 },
  { name: 'Hotel Arcimboldi', city: 'Milano', eff: 84.1, ae: true, rooms: 124 },
  { name: 'Hotel Galleria', city: 'Milano', eff: 81.5, ae: false, rooms: 96 },
  { name: 'Hotel Heart', city: 'Firenze', eff: 77.3, ae: false, rooms: 64 },
  { name: 'Hotel Kira', city: 'Roma', eff: 76.8, ae: true, rooms: 91 },
  { name: 'Hotel Vivaldi', city: 'Venezia', eff: 71.6, ae: false, rooms: 52 },
];

const HotelRanking = () => (
  <div style={{ background: C.surf, borderRadius: 20, padding: 22, border: `1px solid ${C.border}` }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
      <div>
        <div style={{ fontFamily: fontHead, fontSize: 19, fontWeight: 600, letterSpacing: '-0.01em' }}>
          Ranking efficienza hotel
        </div>
        <div style={{ fontSize: 11.5, color: C.textSec, marginTop: 2 }}>Indice 0-100 · ultimi 12 mesi</div>
      </div>
      <button style={{ fontSize: 11.5, color: C.gold, background: 'transparent',
                       border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        Vedi tutti <ArrowUpRight size={11} />
      </button>
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {hotels.map((h, i) => (
        <div key={h.name} style={{
          display: 'grid', gridTemplateColumns: '24px 1fr auto auto', gap: 14, alignItems: 'center',
          padding: '10px 12px', borderRadius: 10, background: 'rgba(255,255,255,0.02)',
          border: `1px solid ${C.border}`,
        }}>
          <span style={{ fontFamily: fontHead, fontSize: 13, fontWeight: 600,
                         color: i < 3 ? C.gold : C.textSec, textAlign: 'center' }}>#{i + 1}</span>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontWeight: 600, fontSize: 13.5 }}>{h.name}</span>
              {h.ae && <span style={{
                fontSize: 8.5, fontWeight: 700, letterSpacing: '0.1em',
                padding: '2px 6px', borderRadius: 4,
                background: `${C.gold}22`, color: C.gold, border: `1px solid ${C.gold}55`,
                display: 'inline-flex', alignItems: 'center', gap: 3,
              }}><Zap size={8} /> ALL-ELECTRIC</span>}
            </div>
            <div style={{ fontSize: 11, color: C.textSec, marginTop: 2 }}>{h.city} · {h.rooms} camere</div>
          </div>
          <div style={{ width: 100, height: 5, background: C.border, borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ width: `${h.eff}%`, height: '100%',
                          background: i < 3 ? C.gold : (h.eff > 75 ? C.cyan : C.terra) }} />
          </div>
          <span style={{ fontFamily: fontHead, fontWeight: 600, fontSize: 14,
                         color: i < 3 ? C.gold : C.text, minWidth: 38, textAlign: 'right' }}>
            {h.eff}
          </span>
        </div>
      ))}
    </div>
  </div>
);

const CriticalFeed = () => {
  const items = [
    { hotel: 'Galleria', room: '208', cat: 'Idraulica', title: 'Perdita continua scarico lavabo', time: '2m', sla: '-3m' },
    { hotel: 'Arcimboldi', room: 'Lobby', cat: 'HVAC', title: 'AC centrale spenta da 14 min', time: '14m' },
    { hotel: 'Scarlatti', room: '412', cat: 'Elettrico', title: 'Presa fuori uso, ospite anziano', time: '42m' },
    { hotel: 'Kira', room: '305', cat: 'Ascensori', title: 'Pulsantiera cabina B intermittente', time: '1h 4m' },
  ];
  return (
    <div style={{ background: C.surf, borderRadius: 20, padding: 22, border: `1px solid ${C.terra}33`,
                  position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, right: 0, width: 200, height: 100,
                    background: `radial-gradient(ellipse at top right, ${C.terra}15, transparent 60%)`,
                    pointerEvents: 'none' }} />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <CircleDot size={12} color={C.terra} style={{ animation: 'pulse 1.5s infinite' }} />
          <div>
            <div style={{ fontFamily: fontHead, fontSize: 19, fontWeight: 600, letterSpacing: '-0.01em' }}>
              Critical feed
            </div>
            <div style={{ fontSize: 11.5, color: C.textSec, marginTop: 2 }}>Ticket urgenti cross-hotel · live</div>
          </div>
        </div>
        <span style={{ fontFamily: fontHead, fontSize: 22, fontWeight: 600, color: C.terra }}>
          {items.length}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
        {items.map((it, i) => (
          <div key={i} style={{
            display: 'grid', gridTemplateColumns: '4px 1fr auto', gap: 12, alignItems: 'center',
            padding: '11px 12px 11px 0', borderRadius: 10, background: 'rgba(226,125,96,0.05)',
            border: `1px solid ${C.terra}22`,
          }}>
            <div style={{ background: C.terra, height: '90%', borderRadius: 12 }} />
            <div style={{ paddingLeft: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                <span style={{ fontWeight: 600, fontSize: 12.5 }}>{it.hotel}</span>
                <span style={{ fontSize: 11, color: C.textSec }}>· {it.room} · {it.cat}</span>
                {it.sla && <span style={{
                  fontSize: 9.5, color: C.terra, fontWeight: 700,
                  padding: '2px 6px', background: `${C.terra}22`, borderRadius: 4,
                }}>SLA {it.sla}</span>}
              </div>
              <div style={{ fontSize: 12.5, color: C.text, opacity: 0.92 }}>{it.title}</div>
            </div>
            <span style={{ fontSize: 11, color: C.textSec, paddingRight: 8 }}>{it.time}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- BOTTOM ROW
const BottomRow = () => (
  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16 }}>
    <Heatmap />
    <DocsVault />
  </div>
);

const Heatmap = () => {
  const cats = ['Idraulica', 'Elettrico', 'HVAC', 'Estetica', 'Ascens.', 'Altro'];
  // matrice [hotel][cat] intensità 0-1
  const grid = [
    [0.8, 0.3, 0.6, 0.2, 0.1, 0.1],
    [0.5, 0.4, 0.7, 0.1, 0.2, 0.2],
    [0.9, 0.2, 0.5, 0.3, 0.4, 0.1],
    [0.3, 0.5, 0.4, 0.4, 0.0, 0.3],
    [0.6, 0.7, 0.5, 0.2, 0.6, 0.1],
    [0.2, 0.3, 0.2, 0.5, 0.0, 0.2],
  ];
  return (
    <div style={{ background: C.surf, borderRadius: 20, padding: 22, border: `1px solid ${C.border}` }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
        <div>
          <div style={{ fontFamily: fontHead, fontSize: 19, fontWeight: 600, letterSpacing: '-0.01em' }}>
            Heatmap problemi gruppo
          </div>
          <div style={{ fontSize: 11.5, color: C.textSec, marginTop: 2 }}>Ultimi 30 giorni · concentrazione per hotel × categoria</div>
        </div>
        <button style={{ fontSize: 11.5, color: C.gold, background: 'transparent', border: 'none',
                         cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          Vista dettaglio <ArrowUpRight size={11} />
        </button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '130px repeat(6, 1fr)', gap: 6, fontSize: 11 }}>
        <div />
        {cats.map(c => <div key={c} style={{ color: C.textSec, textAlign: 'center', padding: 6 }}>{c}</div>)}
        {hotels.map((h, i) => (
          <React.Fragment key={h.name}>
            <div style={{ color: C.text, padding: '10px 6px', fontWeight: 500, fontSize: 12 }}>
              {h.name.replace('Hotel ', '')}
            </div>
            {grid[i].map((v, j) => {
              // dark blue → gold → terracotta
              const color = v < 0.25 ? `rgba(56,189,248,${0.05 + v})` :
                           v < 0.6 ? `rgba(212,175,55,${0.15 + v * 0.4})` :
                           `rgba(226,125,96,${0.3 + v * 0.5})`;
              return (
                <div key={j} style={{
                  background: color, borderRadius: 6, padding: '14px 6px', textAlign: 'center',
                  fontSize: 11.5, fontWeight: 600, color: v > 0.5 ? '#fff' : C.textSec,
                  border: `1px solid ${v > 0.7 ? C.terra : C.border}`,
                }}>
                  {Math.round(v * 30)}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

const DocsVault = () => {
  const docs = [
    { name: 'Pitch Deck', sub: 'Investitori · 12 slide', acc: C.gold },
    { name: 'Business Case', sub: 'CFO · 18 pp', acc: C.cyan },
    { name: 'One-Pager', sub: 'Sintesi vendite', acc: C.gold },
    { name: 'Guida Front Office', sub: 'Ticket · v.06/2026', acc: C.emerald },
    { name: 'Spec Indice Efficienza', sub: 'Engineering', acc: C.cyan },
    { name: 'Changelog v3.0', sub: 'Internal · 06/2026', acc: C.textSec },
  ];
  return (
    <div style={{ background: C.surf, borderRadius: 20, padding: 22, border: `1px solid ${C.border}` }}>
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontFamily: fontHead, fontSize: 19, fontWeight: 600, letterSpacing: '-0.01em' }}>
          Documenti
        </div>
        <div style={{ fontSize: 11.5, color: C.textSec, marginTop: 2 }}>Vault · auto-generati</div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {docs.map((d, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
            background: 'rgba(255,255,255,0.02)', borderRadius: 10,
            border: `1px solid ${C.border}`, cursor: 'pointer',
          }}>
            <div style={{
              width: 30, height: 30, borderRadius: 7, background: `${d.acc}1a`,
              display: 'grid', placeItems: 'center',
            }}><FileText size={14} color={d.acc} /></div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: C.text }}>{d.name}</div>
              <div style={{ fontSize: 10.5, color: C.textSec, marginTop: 1 }}>{d.sub}</div>
            </div>
            <ArrowUpRight size={13} color={C.textSec} />
          </div>
        ))}
      </div>
    </div>
  );
};
